import {
  Button,
  CriticalButton,
  Modal,
  SkeletonBar,
  Text,
  useModal,
  vars,
} from "@janeapp/burrito-design-system"
import { formatRelative, secondsToDate } from "core"
import { DeviceRow } from "core/types"
import { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import styled, { css } from "styled-components"
import { AccountContext } from "../../context/AccountContext"
import {
  AccountCard,
  CARD_WIDE_BREAKPOINT,
  CardError,
  Label,
} from "./AccountCard"

// The button sits under the text until there is room to put it alongside.
const DeviceTile = styled.div(
  () => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${vars.global.size["2x"]};
    border: ${vars.alias.border.width.sm} solid
      ${vars.alias.border.color.default};
    border-radius: ${vars.alias.border.radius.md};
    padding: ${vars.global.size["4x"]};

    @media (min-width: ${CARD_WIDE_BREAKPOINT}) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  `
)

const SEPARATOR = " \u00b7 "

const Device = ({
  row,
  onSignOut,
}: {
  row: DeviceRow
  onSignOut: (row: DeviceRow) => void
}) => {
  const { t, i18n } = useTranslation()

  const activity = row.isCurrent
    ? t("session.activeNow")
    : t("session.lastActive", {
        relative: formatRelative(
          secondsToDate(row.lastAccess),
          new Date(),
          i18n.language
        ),
      })

  return (
    <DeviceTile>
      <div className="flex min-w-0 flex-col gap-2">
        <Label elementType="span" size="lg">
          {[row.name, row.ipAddress].filter(Boolean).join(SEPARATOR)}
        </Label>
        <Text elementType="span" size="md">
          {row.browser}
          {SEPARATOR}
          <Text
            elementType="span"
            size="md"
            color={row.isCurrent ? "success" : "default"}
          >
            {activity}
          </Text>
        </Text>
      </div>
      <Button
        onClick={() => onSignOut(row)}
        aria-label={`${t("session.signOut")} ${row.name}`}
      >
        {t("session.signOut")}
      </Button>
    </DeviceTile>
  )
}

type ModalTarget = "all" | DeviceRow | null

export const RegisteredDevices = () => {
  const { t } = useTranslation()
  const { deviceRows, loading, errors, signOutEverywhere, signOutSession } =
    useContext(AccountContext)
  const { state: modalState, modalProps } = useModal()
  const [modalTarget, setModalTarget] = useState<ModalTarget>(null)

  const openSignOutModal = (target: "all" | DeviceRow) => {
    setModalTarget(target)
    modalState.open()
  }

  const handleConfirm = async () => {
    modalState.close()
    if (modalTarget === "all") {
      await signOutEverywhere()
    } else if (modalTarget) {
      await signOutSession(modalTarget.sessionId)
    }
  }

  const isAll = modalTarget === "all"
  const isCurrent = typeof modalTarget === "object" && modalTarget?.isCurrent

  const modalTitle = isAll
    ? t("session.signOutAllTitle")
    : t("session.signOutTitle")

  const modalDescription = isAll
    ? t("session.signOutAllDescription")
    : isCurrent
    ? t("session.signOutCurrentDescription")
    : t("session.signOutOtherDescription", {
        device:
          typeof modalTarget === "object" && modalTarget
            ? [
                modalTarget.name,
                modalTarget.browser !== "Other" ? modalTarget.browser : "",
              ]
                .filter(Boolean)
                .join(" · ")
            : "",
      })

  const confirmText = isAll
    ? t("loginSecurity.signOutFromAll")
    : t("session.signOut")

  const signOutAll = !loading && !errors.devices && deviceRows.length > 0 && (
    <CriticalButton isPlain onClick={() => openSignOutModal("all")}>
      {t("loginSecurity.signOutFromAll")}
    </CriticalButton>
  )

  return (
    <>
      <AccountCard
        title={t("loginSecurity.whereYouAreSignedIn")}
        action={signOutAll || undefined}
        contentGap={vars.global.size["2x"]}
      >
        {errors.devices ? (
          <CardError />
        ) : loading ? (
          <SkeletonBar size="lg" />
        ) : (
          deviceRows.map((row) => (
            <Device
              key={row.sessionId}
              row={row}
              onSignOut={openSignOutModal}
            />
          ))
        )}
      </AccountCard>

      <Modal state={modalState} {...modalProps} size="md">
        <Modal.Header>
          <Modal.Title>{modalTitle}</Modal.Title>
          <Modal.CloseButton onClick={modalState.close} />
        </Modal.Header>
        <Modal.Content>
          <Text size="md">{modalDescription}</Text>
        </Modal.Content>
        <Modal.Footer>
          <Button onClick={modalState.close}>{t("session.cancel")}</Button>
          <CriticalButton onClick={handleConfirm}>{confirmText}</CriticalButton>
        </Modal.Footer>
      </Modal>
    </>
  )
}
